import {Component, ConfigOptions} from 'panel';

import {promisifyModal} from 'common/components/modal/utils';
import {capitalize} from 'common/util';
import {registerMPElement} from 'common/util/register-element';
import {MAX_COHORT_NAME_LENGTH, MAX_DASHBOARD_NAME_LENGTH, MAX_ENTITY_NAME_LENGTH} from 'common/constants';

import template from './index.jade';

import css from './index.styl?inline';
import {CreateEntityModalState, CreateEntityModalAttrs, ModalReturn, ModalValues} from './types';

const INVALID_INPUTS = {
  NAME: `name`,
  DESCRIPTION: `description`,
};

const ENTITY_TYPE_TO_OPTIONS = {
  cohort: {article: `a`, plural: `cohorts`, maxNameLength: MAX_COHORT_NAME_LENGTH, enableVisibilityControls: true},
  'custom property': {
    article: `a`,
    plural: `custom properties`,
    enableVisibilityControls: true,
  },
  'data group': {
    plural: `data groups`,
    enableVisibilityControls: false,
  },
  report: {
    article: `a`,
    plural: `reports`,
    maxNameLength: MAX_ENTITY_NAME_LENGTH,
    enableVisibilityControls: true,
  },
  dashboard: {
    article: `a`,
    plural: `dashboards`,
    maxNameLength: MAX_DASHBOARD_NAME_LENGTH,
    enableVisibilityControls: true,
  },
  workspace: {article: `a`, plural: `workspaces`, maxNameLength: 160, enableVisibilityControls: false},
  journey: {
    article: `a`,
    plural: `journeys`,
    maxNameLength: MAX_ENTITY_NAME_LENGTH,
    enableVisibilityControls: false,
  },
};

export class CreateEntityModal extends Component<CreateEntityModalState, {}, unknown, CreateEntityModalAttrs> {
  asyncSave?: (value: ModalValues) => Promise<void>;
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'entity-type': {
        type: `string`,
        description: `Type of entity being created`,
        enum: Object.keys(ENTITY_TYPE_TO_OPTIONS),
      },
      'initial-description': {type: `string`, description: `Initial description of entity`, default: ``},
      'initial-name': {type: `string`, description: `Initial name of entity`, default: ``},
      'initial-hidden': {type: `boolean`, description: `Initial hidden state of entity`, default: true},
      'initial-locked': {type: `boolean`, description: `Initial restricted state of entity`, default: true},
      'modal-title': {type: `string`, description: `Title of modal`},
      open: {type: `boolean`},
      warning: {type: `string`},
      'hidden-disabled': {type: `boolean`, description: `Is the hidden radio button disabled`},
      'lock-disabled': {type: `boolean`, description: `Is the restricted checkbox disabled`},
    };
  }

  get config(): ConfigOptions<CreateEntityModalState, {}, CreateEntityModalAttrs> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        description: ``,
        existingEntityNames: new Set(),
        invalidInputWarning: null,
        saving: null,
        name: ``,
        open: false,
        hidden: true,
        locked: true,
      },
      helpers: {
        capitalize,
        getEntityTypeArticleStr: () => ENTITY_TYPE_TO_OPTIONS[this.attr(`entity-type`)].article,
        getEntityTypePluralStr: () => ENTITY_TYPE_TO_OPTIONS[this.attr(`entity-type`)].plural,
        areVisibilityControlsEnabled: () => ENTITY_TYPE_TO_OPTIONS[this.attr(`entity-type`)].enableVisibilityControls,
        getMaxNameLength: () => ENTITY_TYPE_TO_OPTIONS[this.attr(`entity-type`)].maxNameLength,
        getNameError: () => {
          if (this.helpers.hasSameNameAsExistingEntity()) {
            return `${capitalize(this.helpers.getEntityTypeArticleStr())} ${this.attr(
              `entity-type`,
            )} with that name already exists`;
          }
          return `Name is required for saving ${this.helpers.getEntityTypeArticleStr()}} ${this.attr(`entity-type`)}`;
        },
        hasValidName: () => !this.helpers.hasSameNameAsExistingEntity() && this.state.name.trim().length > 0,
        hasSameNameAsExistingEntity: () => this.state.existingEntityNames.has(this.state.name.trim().toLowerCase()),
        handleDescriptionInput: (ev) => {
          this.update({description: ev.target.value, invalidInputWarning: null});
        },
        warnInvalidInput: () => {
          const invalidInputWarning = this.helpers.hasSameNameAsExistingEntity() ? INVALID_INPUTS.NAME : null;
          this.update({invalidInputWarning});
        },
        handleModalChange: (ev) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.update({
              name: this.attr(`initial-name`),
              description: this.attr(`initial-description`),
              invalidInputWarning: null,
            });
            this.dispatchEvent(new CustomEvent(`modalClosed`));
          }
        },
        cancelChanges: () => {
          // Did not add hidden and locked here (Haven't seen the need), might need it in the future
          const {name, description} = this.state;
          this.update({invalidInputWarning: null});
          this.dispatchEvent(new CustomEvent(`cancel`, {detail: {name, description}}));
        },
        saveChanges: () => {
          const {name, description, hidden, locked} = this.state;
          const value = {name, description, hidden, locked};
          this.saveValue(value);
        },
        handleNameInput: (ev) => {
          this.update({name: ev.target.value, invalidInputWarning: null});
        },
        handleVisibilityHiddenChange: (ev) => {
          this.update({hidden: ev.detail.value});
        },
        handleVisibilityLockedChange: (ev) => {
          this.update({locked: ev.detail.value});
        },
        handleKeydown: (ev) => {
          if (ev.keyCode === 13) {
            ev.preventDefault();
            if (this.helpers.hasValidName()) {
              // save changes if enter
              this.helpers.saveChanges();
            } else {
              this.helpers.warnInvalidInput();
            }
          } else if (ev.keyCode === 27) {
            // on escape, cancel changes
            this.helpers.cancelChanges();
          }
        },
      },
    };
  }

  set existingEntityNames(val: string[]) {
    this.update({existingEntityNames: new Set(val.map((name) => name.toLowerCase()))});
  }

  private elRefs: {[ref: string]: HTMLElement} = {};

  attributeChangedCallback(name, oldValue, newValue): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `initial-name`) {
      this.update({name: newValue});
    } else if (name === `initial-description`) {
      this.update({description: newValue});
    } else if (name === `initial-hidden`) {
      this.update({hidden: newValue === `true`});
    } else if (name === `initial-locked`) {
      this.update({locked: newValue === `true`});
    }
  }

  async saveValue(value) {
    if (this.asyncSave) {
      this.dispatchEvent(new CustomEvent(`saving`, {detail: {value}}));
      const saving = this.asyncSave(value);
      this.update({saving});
      try {
        await saving;
      } catch (err) {
        this.dispatchEvent(new CustomEvent(`saveFailed`, {detail: {value, err}}));
        this.update({saving: null});
        return;
      }
    }
    this.dispatchEvent(new CustomEvent(`save`, {detail: {value}}));
  }

  setElRef(ref: string, el: HTMLElement) {
    this.elRefs[ref] = el;
  }
}

export default registerMPElement(`mp-create-entity-modal`, CreateEntityModal);

export async function createEntityModalPromise(
  attrs: Partial<CreateEntityModalAttrs>,
  props: any = {},
): Promise<ModalReturn> {
  const closeEvent = await promisifyModal({
    tagName: `mp-create-entity-modal`,
    attrs,
    props,
    closeEvents: [`save`, `cancel`],
  });
  const values = closeEvent.type === `save` ? closeEvent.detail.value : closeEvent.detail;
  return {didSave: closeEvent.type === `save`, ...values};
}

export function promptManageDashboardModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
  props: any = {},
): Promise<ModalReturn> {
  return createEntityModalPromise(
    {
      ...attrs,
      'entity-type': `dashboard`,
      'modal-title': title,
    },
    props,
  );
}

export function promptManageCohortModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
): Promise<ModalReturn> {
  return createEntityModalPromise({
    ...attrs,
    'entity-type': `cohort`,
    'modal-title': title,
  });
}

export function promptManageVisualJourneyModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
): Promise<ModalReturn> {
  return createEntityModalPromise({
    ...attrs,
    'entity-type': `journey`,
    'modal-title': title,
  });
}
